import 'dayjs/locale/fr';
import 'dayjs/locale/es';
import 'dayjs/locale/en';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import duration from 'dayjs/plugin/duration';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import DOMPurify from 'dompurify';
import { t, TFunction } from 'i18next';
import { isEqual } from 'lodash';
import qs from 'qs';
import { allCountries } from 'country-region-data';
import { SetValueConfig } from 'react-hook-form';
import {
  capitalize,
  isArray,
  isNumber,
  isPlainObject,
  isString,
  keys,
  omitBy,
  toSnakeCase,
} from 'remeda';
import { stripHtml } from 'string-strip-html';
import { ISO_8601 } from './constants';
import {
  AnyRecord,
  AssetType,
  EditorAssetType,
  editorEnabledAssetTypesSet,
} from './types/cosmosTypes';
import {
  EditorialSearchQueryParams,
  PaymentMethod,
  UserAccountFull,
} from './types/dataProvider';
import { GetWorkflowsResult, Workflow } from 'providers/contexts/extensions/Workflows';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(duration);
dayjs.extend(relativeTime);
dayjs.extend(LocalizedFormat);
dayjs.extend(advancedFormat);
dayjs.extend(isSameOrAfter);

/**
 * DOMPurify.sanitize function exported.
 * Fixes the issue with the DOMPurify export defs.
 */
export const DOMSanitize = DOMPurify.sanitize;

/**
 * Checks if a value is a finite number
 * eg to filter NaN, Inf, -Inf, etc...
 * use example:
 * const val = parseInt(event.target.value, 10);
 * const isValidNum = isFiniteNumber(val);
 */
export function isFiniteNumber(value: any): boolean {
  const isNum = isNumber(value) && Number.isFinite(value);
  return isNum;
}

type StorageType = 'localStorage' | 'sessionStorage';
/**
 * Remove all items with a specified prefix from session and local storage.
 * @param {string} prefix - The prefix to match keys against. e.g. 'oidc.'
 * @param {StorageType} stores - The stores to clear. Defaults to ['localStorage', 'sessionStorage'].
 */
export const clearPrefixedStorage = (
  prefix: string,
  stores: StorageType[] = ['localStorage', 'sessionStorage'],
) => {
  if (!prefix || !isString(prefix)) {
    console.warn(`[clearPrefixedStorage] Prefix is not a string: ${prefix}`);
    return;
  }

  if (!stores || !isArray(stores)) {
    console.warn(`[clearPrefixedStorage] Stores is not an array: ${stores}`);
    return;
  }

  // Clear from localStorage
  if (stores.includes('localStorage')) {
    Object.keys(localStorage)
      .filter((key) => key.startsWith(prefix))
      .forEach((key) => {
        localStorage.removeItem(key);
      });
  }

  // Clear from sessionStorage
  if (stores.includes('sessionStorage')) {
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith(prefix))
      .forEach((key) => {
        sessionStorage.removeItem(key);
      });
  }
};

/**
 * Converts a string to lower snake case.
 * eg: "Hello World" -> "hello_world"
 * useful for certain testid builders in loops
 */
export function toLowerSnakeCase(str: string | null | undefined): string {
  if (typeof str !== 'string') {
    return '';
  }
  const snakeCaseLower = toSnakeCase(str).toLowerCase();
  return snakeCaseLower;
}

/**
 * Capitalizes the first letter of a given string and leaves the rest of the string unchanged.
 * @param {string} str - The input string to capitalize.
 * @returns {string} The modified string with the first letter capitalized.
 */

export function capitalizeFirstLetter(str?: string): string {
  if (!str) {
    return '';
  }
  const baseString = String(str);
  return baseString.charAt(0).toUpperCase() + baseString.slice(1);
}

/**
 * Returns the appropriate indefinite article ("a" or "an") for a given word based on English grammar rules.
 * @param {string} word - The word to determine the article for.
 * @returns {string} "a" or "an"
 */
export function getIndefiniteArticle(word?: string): string {
  if (!word) return 'a';
  const normalizedWord = String(word).toLowerCase();
  return /^[aeiou]/i.test(normalizedWord) ? 'an' : 'a';
}

type BaseFormatDateParams = {
  date?: string | number | Date | null;
  shiftTz?: string;
  tzKeepLocalTime?: boolean;
  utcMode?: boolean;
  locale?: string;
};

type DayJSFormatMode = {
  format: 'ISO8601' | string; // Dayjs format strings or 'ISO8601' for .toISOString()
  relative?: never; // Disallow in Dayjs mode
};

type IntlFormatMode = {
  format?: Intl.DateTimeFormatOptions; // Intl format object mode
  relative?: RelativeOptions; // Allowed in Intl mode (see code for details)
};

type RelativeOptions = DayJSRelativeMode | CommentsRelativeMode;

type DayJSRelativeMode = {
  mode: 'dayjs';
  withoutSuffix?: boolean; // only dayjs mode
};

type CommentsRelativeMode = {
  mode: 'comments';
  withoutSuffix?: never; // Disallow in comments mode
};

type FormatDateParams = BaseFormatDateParams & (DayJSFormatMode | IntlFormatMode);

const defaultFormat: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
};

/**
 * Check if a date is able to be converted to a valid date object
 * by dayjs. Returns true if the date is valid, false otherwise.
 *
 * Return false for falsy inputs like empty strings, null, undefined.
 */
export function isValidDate(date?: BaseFormatDateParams['date']): boolean {
  if (!date) {
    return false; // ensure dayjs doesn't default to current date
  }
  const isValid = dayjs(date).isValid();
  return isValid;
}

/**
 * Accept i18n language return dayjs locale string.
 * https://day.js.org/docs/en/i18n/i18n
 * eg: const lang = i18n.resolvedLanguage || i18n.language;
 * const djsLoc = toDayjsLocale(lang);
 * dayjs().locale(djsLoc);
 */
export function toDayJsLocale(lang?: string) {
  const language = lang || 'en';
  const lower = language.toLowerCase();
  switch (lower) {
    case 'fr': {
      return 'fr';
    }
    case 'es': {
      return 'es';
    }
    case 'en': {
      return 'en';
    }
    default: {
      return 'en';
    }
  }
}

/**
 * Formats a date based on the provided options.
 *
 * @param {FormatDateParams['date']} date
 * The date to be formatted.
 * Can pass in a number or Date object as well. Will be parsed by dayjs.
 * Generally expected to be ISO-8601 UTC string "2024-10-21T13:48:35+00:00"
 *
 * @param {FormatDateParams['shiftTz']} shiftTz
 * Specify tz to shift the date into the specified TZ identifier e.g. "America/Chicago".
 * e.g. If the API provides a timezone (generally in UTC), specify via sessionConfig
 * sites timezone "America/Los_Angeles" to shift the date to the correct timezone.
 * This option will be set in the Intl.DateTimeFormat timeZone if not provided in format.
 *
 *  @param {FormatDateParams['format']} format
 * Optional custom format to use for formatting.
 * If you pass an Intl.DateTimeFormatOptions it will use the Intl.DateTimeFormat constructor.
 * When using the object formatter you may also pass in locales and relative options.
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat
 *
 * If you pass a string it will use the dayjs format method.
 * If you explicitly want ISO8601 format, pass 'ISO8601' as the format.
 * This will not use intl and is not compatible with locales or relative options.
 * https://day.js.org/docs/en/display/format
 *
 * @param {FormatDateParams['tzKeepLocalTime']} tzKeepLocalTime
 * If shifting timezone, keep the local time when shifting the date?
 * This is useful for inputs format/parse. Default false.
 * Details: https://github.com/iamkun/dayjs/issues/1149
 *
 * @param {FormatDateParams['utcMode']} utcMode
 * Default True. Setup the dayjs parse in UTC mode.
 * This is generally the desired as the API should send in UTC.
 * https://day.js.org/docs/en/plugin/utc
 *
 * @param {FormatDateParams['locale']} locale
 * Optional locales argument for the locale.
 * Default undefined will use the browsers default locale.
 *
 * @param {FormatDateParams['relative']} relative
 * If comments relative formatting should be applied:
 * Will override format and produce a string "7:00 PM Today" if within +/- 1 day.
 * If not within range, will use the passed in format options.
 *
 * If 'dayjs' string passed, will use the dayjs relativeTime plugin to format the output.
 * Formats the output directly using the dayjs relativeTime plugin e.g. " 5 minutes ago"
 * Additional options can be passed in the relative object, eg. withoutSuffix: true
 * https://day.js.org/docs/en/plugin/relative-time
 *
 * @returns {string}
 * The formatted date string if valid.
 * Returns empty string if missing info or invalid.
 *
 * @description
 * Notes: the API generally sends ISO-8601 UTC normalized (sometimes without the Z, eg +00:00)
 * We generally assume that the frontend should shift that time to the correct TZ for display.
 * The sites timezone can be pulled from useCosmos / sessionConfig?.time_zone / "America/Los_Angeles"
 * https://stackoverflow.com/questions/33114386/datetime-iso-8601-without-timezone-component
 * https://townnews.atlassian.net/browse/CMSADMIN-1126?focusedCommentId=321419
 *
 * Examples:
 * Recieve API: 2021-03-17T22:00:00+00:00
 * Shifting TZ: 2021-03-17T15:00:00-07:00 (eg to America/Los_Angeles)
 * Sending back API generally is in ISO UTC normalized eg.:
 * dayjs(selectedDate).tz(siteTimeZone).startOf('day').toISOString();
 * Local String: 2022-05-17T15:00:00-05:00 -> to ISO (UTC): 2022-05-17T20:00:00.000Z
 *
 * The same applies for most DateTime Inputs, you can see an examples in Dates.tsx
 * The catch with these is that they are in datetime-local format without a tz.
 * On the format prop (record -> input)
 * (val) => formatDate({ date: val, format: 'datetime-local', shiftTz: siteTz });
 * On the parse prop (input -> record)
 * (val) => formatDate({ date: val, format: ISO_8601, tzKeepLocalTime: true, utcMode: false });
 * Since datetime-local has no tz you keep local time and turn off utc mdoe for this one,
 * else when the user moves the date around it will jump around by the offset...
 * With these two, the inputs date should be shifted to site tz for display
 * and then when setting back into the record, it should be shifted back to ISO UTC.
 *
 */

export function formatDate({
  date,
  shiftTz,
  utcMode = true,
  format = defaultFormat,
  tzKeepLocalTime = false,
  locale = undefined,
  relative = undefined,
}: FormatDateParams): string {
  if (!date) {
    return '';
  }

  const dateIsValid = dayjs(date).isValid();
  if (!dateIsValid) {
    return '';
  }

  // By default dayjs parses and displays in local (browser) tz
  // setup the date in UTC mode if utcMode is true
  let dayJsDate = utcMode ? dayjs.utc(date) : dayjs(date);

  // Shift dayjs into specified timezone if provided
  if (shiftTz) {
    dayJsDate = dayJsDate.tz(shiftTz, tzKeepLocalTime);
  }

  // Apply locale to dayjs if provided
  if (locale) {
    dayJsDate = dayJsDate.locale(locale);
  }

  // If format is a string use dayjs formatting mode
  const useDayjsFormat = isString(format);
  if (useDayjsFormat) {
    switch (format) {
      case ISO_8601: {
        return dayJsDate.toISOString(); // handle ISO8601 request
      }
      default: {
        return dayJsDate.format(format); // custom dayjs format
      }
    }
  }

  // Setup JS Date for intl formatters
  const jsDate = dayJsDate.toDate();

  // Setup intlFormatter based on the provided options
  // Note: timeZone should generally be the same as shiftTz
  // as the jsDate would be in the browsers timezone with an offset
  const intlFormatter = new Intl.DateTimeFormat(locale, {
    ...format,
    timeZone: format?.timeZone || shiftTz,
  });

  // Handle relative "basic" or "custom" modes formatting
  switch (relative?.mode) {
    // Handle basic dayjs relative formatting (5 minutes ago)
    // Skips the formatter and uses the dayjs relativeTime plugin
    case 'dayjs': {
      const loc = locale || 'en';
      const relStr = dayJsDate.locale(loc).fromNow(relative?.withoutSuffix);
      return relStr;
    }

    // Handle comments mode relative formatting +/- 1d (8:20 AM Today)
    case 'comments': {
      const today = dayjs().tz(shiftTz).startOf('day');
      const yesterday = today.subtract(1, 'day');
      const tomorrow = today.add(1, 'day');

      // Override format options to only show the time part
      // also keep subset of other options from the original format
      const customFmtOpts: Intl.DateTimeFormatOptions = {
        hour: 'numeric',
        minute: 'numeric',
        hour12: format?.hour12,
        second: format?.second,
        timeZone: format?.timeZone || shiftTz, // See comment intlFormatter
        timeZoneName: format?.timeZoneName,
        fractionalSecondDigits: format?.fractionalSecondDigits,
        calendar: format?.calendar,
        formatMatcher: format?.formatMatcher,
        localeMatcher: format?.localeMatcher,
        numberingSystem: format?.numberingSystem,
      };

      const timeFormatter = new Intl.DateTimeFormat(locale, customFmtOpts);

      if (dayJsDate.isSame(yesterday, 'day')) {
        const timeString = timeFormatter.format(jsDate);
        return `${timeString} Yesterday`;
      }

      if (dayJsDate.isSame(today, 'day')) {
        const timeString = timeFormatter.format(jsDate);
        return `${timeString} Today`;
      }

      if (dayJsDate.isSame(tomorrow, 'day')) {
        const timeString = timeFormatter.format(jsDate);
        return `${timeString} Tomorrow`;
      }

      // If no custom relative case matches fall through to standard format.
      break;
    }
  }

  // Final default to the standard intl formatter
  const finalDateString = intlFormatter.format(jsDate);
  return finalDateString;
}

export interface DebounceOptions {
  /** If true, the function will be called on the leading edge of the timeout. Default is false. */
  leading?: boolean;
}

/**
 * Debounces a function, ensuring that it's only called once after the specified delay.
 * For a better hook version of this see: useDebouncedCallback from mantine
 * @param func - The function to debounce.
 * @param delay - The amount of time in milliseconds to wait before invoking the function.
 * @param options - Optional configuration for the debounce behavior.
 * @returns A debounced version of the specified function.
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  delay: number,
  options: DebounceOptions = {},
): (...funcArgs: Parameters<T>) => void {
  let timerId: ReturnType<typeof setTimeout> | undefined;

  return (...args: Parameters<T>) => {
    if (!timerId && options.leading) {
      func(...args);
    }
    if (timerId !== undefined) {
      clearTimeout(timerId);
    }

    timerId = setTimeout(() => func(...args), delay);
  };
}

/**
 * Checks if a given asset type has an editor that is enabled.
 * Valid if the string exists in the editorEnabledAssetTypesSet set.
 *
 * returns true if 'article' is in the array of enabled asset editor types
 * isAssetTypeEditorEnabled('article');
 */
export function isAssetTypeEditorEnabled(key?: string | null): key is EditorAssetType {
  if (!key || typeof key !== 'string') {
    return false;
  }
  const normalized = key?.toLowerCase()?.trim() as EditorAssetType;
  return editorEnabledAssetTypesSet.has(normalized);
}

/**
 * Removes query parameters from a URL string.
 * @param urlString - The URL string to remove query parameters from.
 * @returns The URL string without any query parameters.
 * Console logs an error if the input is not a string or if the URL cannot be parsed and returns the original string.
 */
export const removeQpFromUrlString = (urlString: string | undefined) => {
  try {
    if (!urlString) {
      return urlString;
    }
    if (typeof urlString !== 'string') {
      console.warn(`Expected string type but recieved (${typeof urlString})`);
      return urlString;
    }
    if (
      urlString.startsWith('blob:') ||
      urlString.startsWith('<iframe') ||
      urlString.startsWith('<video')
    ) {
      return urlString; // Blobs, iframes, and video HTML tags shouldnt have query params...
    }
    // Parse the URL, note new url can throw.
    let parsedUrl = new URL(urlString);
    // Construct and return the URL without query parameters
    return `${parsedUrl?.origin}${parsedUrl?.pathname}`;
  } catch (error) {
    // Return the original string as is.
    console.warn('[removeQpFromUrlString] Error:', error);
    return urlString;
  }
};

/**
 * Checks if a given URL string is a valid URL. Optionally checks if the URL's protocol
 * is among the allowed protocols if a list is provided.
 *
 * @param urlString - The URL string to check.
 * @param protocols - Optional array of protocols to check against, e.g., ['http:', 'https:']
 * @param baseString - Optional base URL to use for resolving the URL when urlString is relative.
 * @returns True if the URL is valid; if protocols are provided, true only if the URL's protocol matches one of the specified protocols.
 * @example isValidUrl('http://example.com', ['http:', 'https:']) { ... }
 */
export const isValidUrl = (
  urlString: string,
  protocols?: string[],
  baseString?: string,
): boolean => {
  try {
    const url = new URL(urlString, baseString);
    if (protocols && protocols.length) {
      return protocols.includes(url.protocol);
    }
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Returns the route from a given path, removing the domain.
 *
 * @param path
 * @returns Route
 */
export const getRoute = (path: string) => {
  const pathSections = path.split('/').filter((section) => section !== '');
  // Remove the first section (domain)
  pathSections.shift();
  // Join the remaining sections back together
  const route = '/' + pathSections.join('/');
  return route;
};

/**
 * Extracts the site/default site part from a given pathname.
 *
 * @param {string} pathname - The pathname to extract the site/default site part from.
 * @returns {string} The extracted site/default site part.
 */
export const getDomainFromPath = (pathname: string): string => {
  // Assuming the site/default site part is the first segment of the pathname
  const segments = pathname.split('/');
  if (segments.length >= 2) {
    return segments[1]; // Adjust the index based on the actual structure of your path
  }
  return '';
};

/**
 * Converts a string to title case.
 * @param {string} str - The input string.
 * @returns {string} - The title case string.
 */
export const toTitleCase = (str?: string): string => {
  // Add a guard clause to handle undefined or empty strings
  if (!str) return '';
  // Skip conjunctions, articles, and prepositions
  const skipWords = new Set([
    'and',
    'as',
    'but',
    'for',
    'if',
    'nor',
    'or',
    'so',
    'yet',
    'a',
    'an',
    'the',
    'at',
    'by',
    'in',
    'of',
    'off',
    'on',
    'per',
    'to',
    'up',
    'via',
  ]);
  // More rules could likely be added (also internationalization is not considered here)

  let result = ''; // Initialize an empty string to store the result

  // Split the string into words
  const words =
    str
      .split(/\b/)
      // Convert snake_case to space-separated words.
      ?.map((word) =>
        word.replace(/^_*(.)|_+(.)/g, (match, str1, str2) =>
          str1 ? str1.toUpperCase() : ' ' + str2.toUpperCase(),
        ),
      ) || [];

  // Convert the first word to uppercase and append it to the result
  if (words.length > 0 && /^\w+$/.test(words[0])) {
    result += words[0].charAt(0).toUpperCase() + words[0].slice(1).toLowerCase();
  } else {
    result += words[0]; // Keep non-word parts unchanged
  }

  // Process the rest of the words
  for (let i = 1; i < words.length; i++) {
    const part = words[i];
    if (/^\w+$/.test(part)) {
      // Convert to uppercase if not in skipWords set
      result += skipWords.has(part.toLowerCase())
        ? part.toLowerCase()
        : part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    } else {
      // Keep non-word parts unchanged
      result += part;
    }
  }

  // Ensure the final word is capitalized
  if (words.length > 1 && /^\w+$/.test(words[words.length - 1])) {
    result =
      result.slice(0, -words[words.length - 1].length) +
      words[words.length - 1].charAt(0).toUpperCase() +
      words[words.length - 1].slice(1).toLowerCase();
  }
  return result;
};

/**
 * Updates the document title based on the current route and active Blox domain.
 *
 * @param record - The record to use for the title.
 * @param assetType - The asset type to use for the title.
 */
export const updateDocumentTitle = (record?: any, assetType?: string) => {
  let title = '';
  const stinger = 'BLOX NXT';
  const activeBloxDomain = getDomainFromPath(window.location.pathname);
  const route = getRoute(window.location.pathname);

  if (!assetType) {
    const queryParams = qs.parse(window.location.search, { ignoreQueryPrefix: true });
    const rawAssetType = queryParams.type;
    assetType =
      rawAssetType && typeof rawAssetType === 'string'
        ? toTitleCase(rawAssetType)
        : 'Asset';
  } else {
    assetType = toTitleCase(assetType);
  }

  switch (true) {
    case route === '/' || route === '':
      title = `Dashboard - ${activeBloxDomain}`;
      break;
    case route === '/site-select':
      title = 'Content Management / Search';
      break;
    case route.startsWith('/editorial/create'):
      title = `New ${assetType}`;
      break;
    case route.startsWith('/editorial/edit'):
      if (!record) return;
      const assetTitle = record?.title || '';
      title = assetTitle;
      break;
    case route.startsWith('/editorial/search/filters'):
      title = 'Content Management / Editorial / Quick Filters';
      break;
    case route.startsWith('/editorial/search/templates'):
      title = 'Content Management / Editorial / Templates';
      break;
    case route.startsWith('/editorial/search'):
      title = 'Content Management / Editorial / Search';
      break;
    case route === '/design':
      title = 'Design';
      break;
    case route.startsWith('/design/page-builder'):
      title = 'Design / Page Builder';
      break;
    case route.startsWith('/journeys'):
      title = 'Journeys';
      break;
    case route.startsWith('/notifier/search'):
      title = 'Notifier / Search';
      break;
    case route.startsWith('/notifier/channels'):
      title = 'Notifier / Channels';
      break;
    case route.startsWith('/notifier/connections'):
      title = 'Notifier / Connections';
      break;
    case route === '/user/dashboard':
      title = 'Contacts / Dashboard';
      break;
    case route.startsWith('/user/edit'):
      if (!record) return;
      const isNormal = record?.account_type === 'normal';
      const firstName = record?.first_name;
      const lastName = record?.last_name;
      const email = record?.email;
      const identifier = firstName && lastName ? `${firstName} ${lastName}` : email;
      title = isNormal ? `User: ${identifier}` : `Admin: ${identifier}`;
      break;
    case route.startsWith('/user/create'):
      title = `Create User`;
      break;
    case route.startsWith('/user/account/multisite/search'):
      title = t('blox.user.search.multisite.title');
      break;
    case route.startsWith('/user/account/search'):
      title = 'Contacts / Search';
      break;
    case route.startsWith('/user/admin/search') ||
      route.startsWith('/settings/admins/manage'):
      title = 'Admin Accounts / Search';
      break;
    case route.startsWith('/services/search'):
      title = 'Services / Search';
      break;
    case route.startsWith('/services/edit'):
      if (!record) return;
      const serviceName = record?.name;
      title = `Service: ${serviceName}`;
      break;
    case route.startsWith('/transactions/search'):
      title = 'Transactions / Search';
      break;
    case route.startsWith('/transactions/view'):
      if (!record) return;
      {
        const identifier = record?.transaction_id || record?.id;
        const amountLabel = formatCurrency(record?.amount, record?.currency);
        const typeLabel = record?.type ? toTitleCase(String(record.type)) : undefined;
        const stateLabel = record?.state ? toTitleCase(String(record.state)) : undefined;
        const detailLabel = [amountLabel, typeLabel, stateLabel]
          .filter(Boolean)
          .join(' • ');
        if (detailLabel) {
          title = `Transaction: ${detailLabel}`;
        } else if (identifier) {
          title = `Transaction: ${identifier}`;
        } else {
          title = 'Transaction Details';
        }
      }
      break;
    case route.startsWith('/feeds/search'):
      title = 'Content Management / Feeds / Search';
      break;
    case route.startsWith('/community-settings'):
      title = 'Community Settings';
      break;
    case route.startsWith('/dashboards'):
      document.title = 'BLOX NXT - Dashboards';
      return;
    default:
      break;
  }

  if (title) {
    document.title = `${title} - ${stinger}`;
  } else {
    document.title = stinger;
  }
};

/**
 * Clean string from html tags and blox-inline tags
 * @param {string} input - The input string to clean.
 * @returns {string} - The cleaned string.
 */
export const cleanStringFromHtmlAndBloxInline = (input: string): string => {
  const regex = /<blox-inline([^>]*)>.*?<\/blox-inline>/g;
  const removedBloxInline = input.replace(regex, '');
  return stripHtml(removedBloxInline).result;
};

export interface PreviewLinkOptions {
  /** The NXT admin origin for rewriting internal asset links to preview URLs */
  cosmosOrigin: string;
}

export const buildViewAtSiteUrl = (
  domain: string,
  assetUuid: string,
  app: string = 'editorial',
): string => {
  return `https://${domain}/tncms/asset/${app}/${assetUuid}`;
};

/** Pattern to identify internal BLOX CMS asset links */
const TNCMS_ASSET_PATTERN = /\/tncms\/asset\/([^/]+)\/([0-9a-f-]+)\/?/i;

/**
 * Processes anchor elements in preview HTML.
 *
 * When called without options, replaces all hrefs with javascript:void(0) (legacy behavior
 * for design block previews and edit-mode previews).
 *
 * When called with options, uses DOMParser to:
 * - Remove href from anchors outside `.asset-body` (disabling navigation chrome)
 * - Leave anchors inside `.asset-body` clickable with `target="_blank"`
 * - Rewrite internal BLOX CMS asset links (`/tncms/asset/`) to NXT preview URLs
 * - Preserve fragment, mailto, and tel links as-is
 *
 * @param htmlString - The HTML string to process.
 * @param options - Optional configuration for preview link behavior.
 * @returns The processed HTML string.
 */
export const replaceHrefWithVoid = (
  htmlString: string,
  options?: PreviewLinkOptions,
): string => {
  if (!options) {
    return htmlString.replace(
      /<a\s+([^>]*?)href="[^"]*"(.*?)>/gi,
      '<a $1 href="javascript:void(0);"$2>',
    );
  }

  return processPreviewAnchors(htmlString, options.cosmosOrigin);
};

/**
 * Uses DOMParser to process all anchor elements in the preview HTML
 * before it is rendered in the iframe (no script injection, no timing issues).
 */
function processPreviewAnchors(htmlString: string, cosmosOrigin: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');

  // Resolve the base URI for relative URL resolution
  const baseEl = doc.querySelector('base[href]');
  const baseURI = baseEl?.getAttribute('href') || '';

  const anchors = doc.querySelectorAll('a[href]');
  anchors.forEach((anchor) => {
    // Anchors outside .asset-body: remove href to disable navigation chrome
    if (!anchor.closest('.asset-body')) {
      anchor.removeAttribute('href');
      return;
    }

    const href = anchor.getAttribute('href');
    if (!href) return;

    // Preserve fragment, mailto, and tel links as-is
    if (href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) {
      return;
    }

    // All .asset-body links open in a new tab
    anchor.setAttribute('target', '_blank');
    anchor.setAttribute('rel', 'noopener noreferrer');

    // Rewrite internal CMS asset links to NXT preview URLs
    const match = href.match(TNCMS_ASSET_PATTERN);
    if (match) {
      const app = match[1];
      const uuid = match[2];
      try {
        const resolvedUrl = new URL(href, baseURI || undefined);
        const siteHost = resolvedUrl.hostname;
        if (siteHost) {
          anchor.setAttribute(
            'href',
            `${cosmosOrigin}/${siteHost}/${app}/preview/${uuid}`,
          );
        }
      } catch {
        // If URL parsing fails, leave the href as-is (will open externally)
      }
    }
  });

  // Serialize back to string — use full doctype + html to preserve the complete document
  return `<!DOCTYPE html>\n${doc.documentElement.outerHTML}`;
}

/**
 * Sanitize HTML for preview purposes by replacing href
 * attributes with javascript:void(0).
 */
export const sanitizePreviewHtml = (html: string): string => {
  return replaceHrefWithVoid(html);
};

/**
 * Truncate a string to a specified length and add an ellipsis
 * if the string is longer than the specified length.
 *
 * @param input - The input string to truncate.
 * @param length - The maximum length of the truncated string.
 * @returns The truncated string with an ellipsis if necessary.
 */
export const truncateString = (input: string, length?: number): string => {
  const s = (input ?? '').trim();
  if (length == null || !Number.isFinite(length) || length < 1) return s;
  return s.length > length ? `${s.slice(0, length).trim()}\u2026` : s;
};

/** Find links in an HTML string */
export function findLinks(htmlString: string) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');
  const anchorTags = doc.getElementsByTagName('a');
  const links = [];

  for (let i = 0; i < anchorTags.length; i++) {
    const href = anchorTags[i].getAttribute('href');
    if (href) {
      links.push(href);
    }
  }

  return links;
}

/**
 * Format currency string to symbol.
 * @param {number} amountInCents - The amount to format in minor units (like cents).
 * @param {string} currencyCode - Will default to USD if falsy... The currency code to use for formatting. (USD, GBP, EUR, etc.)
 * @param {string} [locale] - Set the locale to use for formatting. Falls back to the Intl resolved options locale.
 * @returns {string} The formatted currency string. e.g., $123,456.79 for USD.
 */
export const formatCurrency = (
  amountInCents: number | string | undefined,
  currencyCode?: string | undefined,
  locale?: string | undefined,
): string | undefined => {
  const amount =
    typeof amountInCents === 'string' ? parseInt(amountInCents) : amountInCents;

  if (!hasValue(amount)) {
    return undefined;
  }

  // Make sure amountInCents is not out of range and is
  if (amount < Number.MIN_SAFE_INTEGER || amount > Number.MAX_SAFE_INTEGER) {
    console.warn(`[formatCurrency] - Amount is out of range: ${amount}`);
    return undefined; // Return an empty string if the amount is out of range
  }

  const amountInDollars = amount / 100;

  const usingLocale = locale || new Intl.NumberFormat()?.resolvedOptions()?.locale;

  const formatter = new Intl.NumberFormat(usingLocale, {
    style: 'currency',
    currency: currencyCode || 'USD', // Default to USD if no currency code is provided. Bewarned, this may be incorrect for some use cases...
    notation: 'standard',
  });
  return formatter.format(amountInDollars);
};

/**
 * Format asset type to translated ui string.
 * eg: 'article' -> 'Article'
 *     'html' -> 'HTML'
 *     'youtube' -> 'YouTube'
 */
export const formatAssetString = (
  assetType: string | null | undefined,
  t: TFunction,
): string => {
  if (!assetType) {
    return '';
  }

  const type = assetType.toLowerCase();
  const tKey = `blox.general.asset_types.${type}`;

  const fallback = capitalize(type);
  const translated = t(tKey, { defaultValue: '' });

  if (!translated) {
    return fallback;
  }

  return translated;
};

/** AssetType + Wildcard as workaround for MUI Select "All" */
export type SearchTypes = AssetType | '*';

//** String Array of SearchTypes */
export const searchTypes: SearchTypes[] = [
  '*', // All if you pass to backend search query type[]: '*'
  'article',
  'image',
  'collection',
  'audio',
  'file',
  'flash',
  'html',
  'link',
  'pdf',
  'poll',
  'table',
  'video',
  'youtube',
  'zip',
];

export interface SearchDropdownOptionsType {
  value: SearchTypes;
  label: string;
}

export const searchDropdownOptions: SearchDropdownOptionsType[] = [
  ...searchTypes.map((type) => ({
    value: type,
    label: formatAssetString(type, t),
  })),
];

export const searchInitialQueryState: EditorialSearchQueryParams = {
  query: '',
  type: ['*'], // All asset types, asterisk is workaround for MUI Select.
  deleted: 0, // Only show non-deleted records.
};

// Get related asset options based on the asset type
export const getRelatedAssetOptions = (type?: AssetType): string[] => {
  const baseOptions = ['siblings', 'parents'];
  const defaultOptions = ['children', ...baseOptions];
  switch (type) {
    case 'collection':
      return baseOptions;
    case 'article':
      return [...defaultOptions, 'related_content'];
    default:
      return defaultOptions;
  }
};

/** setValue react-hook-form commonly used config */
export const shouldValidDirtyTouch: SetValueConfig = {
  shouldValidate: true,
  shouldDirty: true,
  shouldTouch: true,
};

/**
 * Pass in a React.DragEvent and try to parse the dataTransfer as JSON.
 * Returns the parsed JSON or false if it fails.
 */
export const tryParseDropEventAsJson = (event: React.DragEvent): any => {
  try {
    const data = event?.dataTransfer?.getData('application/json');
    if (!data) return false;
    return JSON.parse(data);
  } catch (error) {
    console.warn('[tryParseDropEventAsJson] - Error:', error);
    return false;
  }
};

/**
 * Converts a date string into a pretty-readable format with dayJS.
 * @param dateString - The date string to convert.
 * @returns A pretty-readable string representing the relative time.
 * @example dateToPrettyFormat('2023-07-09T14:30:00Z') => '5 minutes ago', 'Today at 1:00AM', 'one year ago'
 */
export const dateToPrettyFormat = (dateString?: string) => {
  if (!dateString) {
    return '';
  }
  const now = dayjs();
  const date = dayjs(dateString);
  if (date.isSame(now, 'day')) {
    const diffInMinutes = now.diff(date, 'minute');
    if (diffInMinutes < 1) {
      return 'just now';
    } else if (diffInMinutes < 60) {
      return diffInMinutes === 1
        ? `${diffInMinutes} minute ago`
        : `${diffInMinutes} minutes ago`;
    } else {
      return `today at ${date.format('h:mm A')}`;
    }
  } else if (date.isSame(now.subtract(1, 'day'), 'day')) {
    return `yesterday at ${date.format('h:mm A')}`;
  } else {
    return date.fromNow();
  }
};

/**
 * Converts a card type string to a formatted card name.
 *
 * @param {string} cardName - The type of the card (e.g., 'visa', 'master', 'american_express', 'discover').
 * @returns {string} The formatted card name.
 *
 * @example
 * getCardName('visa'); // returns 'Visa'
 * getCardName('master'); // returns 'Mastercard'
 * getCardName('american_express'); // returns 'American Express'
 * getCardName('discover'); // returns 'Discover'
 * getCardName('other_card'); // returns 'Other Card'
 */
export const getCardName = (cardName?: string): string => {
  if (cardName === null || cardName === undefined) {
    return 'Unknown Card';
  }

  switch (cardName) {
    case 'visa': {
      return 'Visa';
    }
    case 'master': {
      return 'Mastercard';
    }
    case 'american_express': {
      return 'American Express';
    }
    case 'discover': {
      return 'Discover';
    }
    default: {
      return capitalizeFirstLetter(cardName);
    }
  }
};

type GetPaymentMethodInfoResult = {
  isGoogleOrApplePay: boolean;
  label: string;
};

/**
 * Get friendly label for a payment method type.
 * @returns string - The formatted payment method label.
 */
export function getPaymentMethodInfo(
  type?: PaymentMethod['type'],
): GetPaymentMethodInfoResult {
  if (!type) {
    return {
      isGoogleOrApplePay: false,
      label: '',
    };
  }
  switch (type) {
    case 'sprel':
    case 'paypal': {
      return {
        isGoogleOrApplePay: false,
        label: 'PayPal',
      };
    }
    case 'apple_pay':
    case 'apple': {
      return {
        isGoogleOrApplePay: true,
        label: 'Apple Pay',
      };
    }
    case 'google_pay':
    case 'google': {
      return {
        isGoogleOrApplePay: true,
        label: 'Google Pay',
      };
    }
    default: {
      return {
        isGoogleOrApplePay: false,
        label: type,
      };
    }
  }
}

/**
 * Formats a phone number string to a standard format.
 *
 * @param {string} value - The phone number string to format.
 * @returns {string} The formatted phone number string.
 *
 * @example
 * formatPhoneNumber('1234567890'); // returns '(123) 456-7890'
 */
export const formatPhoneNumber = (value: string) => {
  if (!value) return value;
  const phoneNumber = value.replace(/[^\d]/g, '');
  const phoneNumberLength = phoneNumber.length;
  if (phoneNumberLength < 4) return phoneNumber;
  if (phoneNumberLength < 7) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  }
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(
    6,
    10,
  )}`;
};

/**
 * Returns `true` if the card is considered expired given the expiration year and month.
 * The month from the backend is assumed to be 1-based (1 = January, 12 = December).
 */
export const isCardExpired = (
  expirationYear?: number,
  expirationMonth?: number,
): boolean => {
  // If either field is missing, treat it as not expired or handle it however your business logic requires
  if (!expirationYear || !expirationMonth) {
    return false;
  }

  // In JavaScript, months are zero-based: 0 = Jan, 11 = Dec
  // We want to compare to the last day of the given month
  // e.g. if expirationMonth = 4, that's April, so in JS that is 3 (April is 3 if zero-based).
  const lastDayOfExpMonth = new Date(expirationYear, expirationMonth, 0); // day=0 means "one day less than 1st"
  const today = new Date();

  return today > lastDayOfExpMonth;
};

/**
 * Uses remeda's omitBy to remove null or undefined values from an object
 * note: will not remove empty arrays, or empty objects
 *
 * use removeAllEmptyNullUndefinedValues to also remove empty arrays and objects
 *
 * @param obj - The object to remove null or undefined values from.
 * @returns The object with keys that have values of null or undefined removed.
 **/
export function removeNullOrUndefinedValues<T extends AnyRecord>(obj: T): Partial<T> {
  return omitBy(obj, (value) => value === null || value === undefined) as Partial<T>;
}

/**
 * Removes any keys of an object that have values such as
 * null, undefined, empty strings, empty arrays, or empty objects.
 * @example
 * const obj = { key1: null, key2: undefined, key3: '', key4: [], key5: {}, key6: 'hello' };
 * const cleanedObj = removeNullUndefinedEmptyValues(obj);
 * console.warn(cleanedObj); // { key6: 'hello' }
 */
export function removeAllEmptyNullUndefinedValues<T extends AnyRecord>(
  obj: T,
): Partial<T> {
  return omitBy(obj, (value) => {
    if (value === null || value === undefined) {
      return true;
    }
    if (typeof value === 'string' && value.trim() === '') {
      return true;
    }
    if (Array.isArray(value) && value.length === 0) {
      return true;
    }
    if (
      typeof value === 'object' &&
      !Array.isArray(value) &&
      Object.keys(value).length === 0
    ) {
      return true;
    }
    return false;
  }) as Partial<T>;
}

/**
 * @param value A value to trim.
 * @returns If typeof value is string, calls trim. Otherwise, returns the value as is.
 */
export function trimIfString<T>(value: T): string | T {
  if (typeof value === 'string') {
    return value?.trim();
  }
  return value;
}

/**
 * Returns a boolean indicating if string, array, or object has a length greater than 0.
 * @description null or undefined values return false.
 * @param checkObjects Optional parameter to check both string and symbol key length in objects.
 */
export function hasLength(value: unknown, checkObjects?: boolean): boolean {
  if (value === null || value === undefined) {
    return false;
  }

  // Handle strings
  if (isString(value)) {
    const check = value?.length > 0;
    return check;
  }

  // Handle arrays
  if (isArray(value)) {
    const check = value?.length > 0;
    return check;
  }

  // Handle plain objects if checkObjects enabled - not concerned with symbol keys
  if (checkObjects && isPlainObject(value)) {
    const keylen = keys(value)?.length;
    const check = keylen > 0;
    return check;
  }

  // fallback...
  return Boolean(value);
}

/**
 * Deduplicates an array of strings.
 * @param arr - The array of strings to deduplicate.
 * @returns  A new array with duplicate strings removed.
 */
export function dedupeStrArr(arr: string[]): string[] {
  if (!arr) return [];
  if (!Array.isArray(arr)) {
    console.warn('[dedupeStrArr]: Expected an array of strings, got:', arr);
    return [];
  }
  return [...new Set(arr)];
}

/**
 * `hasValue` checks if a value is not null, undefined, or an empty string.
 * e.g. a "0" value, "false", considered a valid value.
 **/
export function hasValue<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined && value !== '';
}

/**
 *
 * @param countryName
 * @returns
 */
export const getCountryCode = (countryName: string): string => {
  const country = allCountries.find(([name]) => name === countryName);
  return country ? country[1] : '';
};

/**
 *
 * @param countryCode
 * @param regionCode
 * @returns
 */
export const getRegionCode = (countryCode: string, regionName: string): string => {
  const country = allCountries.find(([, code]) => code === countryCode);
  if (!country) return '';

  const region = country[2].find(([name]) => name === regionName);
  return region ? region[1] : '';
};

export const getCountryName = (countryCode: string): string => {
  const country = allCountries.find(([, code]) => code === countryCode);
  return country ? country[0] : '';
};

export const getRegionName = (countryCode: string, regionCode: string): string => {
  const country = allCountries.find(([, code]) => code === countryCode);
  if (!country) return '';

  const region = country[2].find(([, code]) => code === regionCode);
  return region ? region[0] : '';
};

export const getCountryCodeFromName = (countryName: string): string => {
  const country = allCountries.find(
    ([name]) => name.toLowerCase() === countryName.toLowerCase(),
  );
  return country ? country[1] : countryName;
};

export const getRegionCodeFromName = (country: string, regionName: string): string => {
  const countryData = allCountries.find(([, code]) => code === country);
  if (!countryData) return regionName;

  const region = countryData[2].find(
    ([name]) => name.toLowerCase() === regionName.toLowerCase(),
  );
  return region ? region[1] : regionName;
};

export const getObjectDiff = (previous: any, current: any, compareRef = false) => {
  return Object.keys(previous).reduce((result, key) => {
    if (!current.hasOwnProperty(key)) {
      result.push(key);
    } else if (isEqual(previous[key], current[key])) {
      const resultKeyIndex = result.indexOf(key);

      if (compareRef && previous[key] !== current[key]) {
        result[resultKeyIndex] = `${key} (ref)`;
      } else {
        result.splice(resultKeyIndex, 1);
      }
    }
    return result;
  }, Object.keys(current));
};

export const getOrdinal = (num: number): string => {
  if (num === 0) return '0';
  const pr = new Intl.PluralRules('en', { type: 'ordinal' });
  const suffixes: Record<string, string> = {
    one: 'st',
    two: 'nd',
    few: 'rd',
    other: 'th',
  };

  const rule = pr.select(num);

  return num + (suffixes[rule] || 'th');
};

interface FormatAuthorOptions {
  /** Whether to truncate multiple authors, showing only the first one with a count */
  truncate?: boolean;
  /** Default text to return when author info is empty/invalid */
  fallback?: string;
  /** Whether to include email as a formatting option */
  includeEmail?: boolean;
}

/**
 * Universal function for formatting author information with configurable options
 *
 * @param authorData - A single author or array of authors
 * @param options - Configuration options for formatting
 * @returns Formatted author string based on specified options
 *
 * @example
 * // Returns "John Doe"
 * formatAuthorData({ first_name: "John", last_name: "Doe" });
 *
 * @example
 * // Returns "johnsmith" (screen name used when both first AND last names aren't available)
 * formatAuthorData({ first_name: "John", screen_name: "johnsmith" });
 *
 * @example
 * // Returns "John Doe, Jane Smith"
 * formatAuthorData([{ first_name: "John", last_name: "Doe" }, { first_name: "Jane", last_name: "Smith" }]);
 *
 * @example
 * // Returns "John Doe... (2 more)"
 * formatAuthorData([
 *   { first_name: "John", last_name: "Doe" },
 *   { first_name: "Jane", last_name: "Smith" },
 *   { screen_name: "bob123" }
 * ], { truncate: true });
 */
export const formatAuthorData = (
  authorData?: Partial<UserAccountFull> | Partial<UserAccountFull>[] | null,
  options: FormatAuthorOptions = {},
): string => {
  const { truncate = false, fallback = '', includeEmail = true } = options;

  // Handle empty input
  if (!authorData) {
    return fallback;
  }

  // Function to format a single author
  const formatSingleAuthor = (author: Partial<UserAccountFull>): string => {
    // Check if we have minimum required info
    const hasFullName = author?.first_name && author?.last_name;
    const hasScreenName = Boolean(author?.screen_name);
    const hasEmail = includeEmail && Boolean(author?.email);

    if (!hasFullName && !hasScreenName && !hasEmail) {
      return '';
    }

    // Format with priority: full name (only if both first and last) > screen name > email
    let formatted = '';

    if (hasFullName) {
      formatted = `${author.first_name} ${author.last_name}`;
    } else if (hasScreenName) {
      formatted = author.screen_name as string;
    } else if (hasEmail) {
      formatted = author.email as string;
    }

    return formatted.trim();
  };

  // Handle array of authors
  if (Array.isArray(authorData)) {
    if (authorData.length === 0) {
      return fallback;
    }

    const formattedAuthors = authorData.map(formatSingleAuthor).filter(Boolean);

    // Return empty string or fallback if no valid authors after formatting
    if (formattedAuthors.length === 0) {
      return fallback;
    }

    // Handle truncation if requested
    if (truncate && formattedAuthors.length > 1) {
      return `${formattedAuthors[0]}... (${formattedAuthors.length - 1} more)`;
    }

    // Return comma-separated list
    return formattedAuthors.join(', ');
  }

  // Handle single author
  const formattedSingle = formatSingleAuthor(authorData);
  return formattedSingle || fallback;
};

/**
 * Get the related content style's title.
 * @param item The related content style data.
 * @returns string
 */
export const getRcsTitle = (item: Record<string, any>) => {
  if (!item || !isPlainObject(item)) return '';

  return !!item.force_title
    ? item.default_title // Always enforce default_title if force_title is true
    : !!item?.default_title?.length
      ? item.default_title
      : '';
};

/**
 * Convert various value types to boolean with explicit handling of common patterns.
 *
 * Boolean conversion rules:
 * - Booleans: returned as-is
 * - Numbers: false for 0, NaN; true for all other numbers
 * - Strings:
 *   - Truthy: 'true', 'yes', 'on', '1' (case-insensitive)
 *   - Falsy: 'false', 'no', 'off', '0' (case-insensitive)
 *   - Other strings: follow JavaScript Boolean conversion (non-empty = true)
 * - Other types: follow JavaScript Boolean conversion
 *
 * @param value The value to convert
 * @returns boolean representation of the value
 */
export const toBoolean = (value: unknown): boolean => {
  // Handle boolean values directly
  if (typeof value === 'boolean') {
    return value;
  }

  /**
   * Numbers can directly convert to Boolean
   * - 0 and NaN are falsy
   * - All other numbers (pos, neg, inf)truthy
   */
  if (typeof value === 'number') {
    return Boolean(value);
  }

  // Handle strings with explicit true/false patterns
  if (typeof value === 'string') {
    const lowerValue = value.toLowerCase().trim();

    // Explicit truthy strings
    if (['true', 'yes', 'on', '1'].includes(lowerValue)) {
      return true;
    }

    // Explicit falsy strings
    if (['false', 'no', 'off', '0'].includes(lowerValue)) {
      return false;
    }

    // String Boolean conversion
    return Boolean(value);
  }

  // Standard Boolean conversion
  return Boolean(value);
};

/**
 * Safely prepares text for direct use in CSS `content: ...`.
 *
 * - Returns a double-quoted string (e.g., `"Hello \"world\""`).
 * - Escapes embedded quotes/backslashes appropriately.
 * - Normalizes CR/LF newlines to spaces to avoid invalid CSS tokens.
 * - Not an HTML escaper; for CSS `content` only.
 *
 * @param s - Raw text to render via CSS `content`.
 * @returns A safely quoted/escaped CSS string literal.
 * @example
 * // results in: content: "He said \"hi\" there"
 * const cssValue = toCSSContentString('He said "hi"\nthere');
 */
export const toCSSContentString = (s?: string): string => {
  return JSON.stringify((s ?? '').replace(/\r?\n/g, ' '));
};

/**
 * Slugify helper
 * @param input - The input string to slugify.
 * @param maxLength - Optional maximum length of the resulting slug.
 * @returns The slugified string.
 * @example slugify('Hello World!') => 'hello-world'
 * @example slugify('This is a test', 10) => 'this-is-a'
 */
export function slugify(input: string, maxLength?: number): string {
  if (!input) {
    return '';
  }

  let s = input
    .toLowerCase()
    .replace(/\s+/g, '-') // spaces to hyphens
    .trim() // trim extra whitespaces
    .replace(/[^\w-]+/g, '') // drop non-word except hyphens
    .replace(/-+/g, '-') // collapse ---
    .replace(/^-|-$/g, ''); // trim edge hyphens

  if (typeof maxLength === 'number' && maxLength > 0 && s.length > maxLength) {
    s = s.slice(0, maxLength).replace(/-+$/g, ''); // avoid trailing - after cut
  }
  return s;
}

/**
 * Helper to extract and clean coordinate values from a string.
 * Returns the first valid coordinate, strips trailing decimal if present.
 */
export function extractCoordinate(text: string): string {
  const coordinateMatch = text.match(/(-?\d+\.?\d*)/);
  if (coordinateMatch) {
    let result = coordinateMatch[0];
    // Strip trailing decimal if present
    if (result.endsWith('.')) {
      result = result.slice(0, -1);
    }
    return result;
  }
  return '';
}

// Formats the available workflows for a site for the user preferences or bulk upload
export type TWorkflowOptions = { id: string; name: string; firstProcessId: string }[];
export const getWorkflowOptions = (
  fetchedSiteWorkflows: GetWorkflowsResult['data'],
): TWorkflowOptions | undefined => {
  if (!fetchedSiteWorkflows?.length) return;
  return [
    ...(fetchedSiteWorkflows?.map((workflow: Workflow) => ({
      id: workflow.id || '',
      name: workflow.name || '',
      firstProcessId: workflow.processes?.[0]?.id || '',
    })) ?? []),
  ];
};

/**
 * Generates a fallback title string for untitled assets.
 * Used when no metadata is extracted from an uploaded file.
 * @param t - The translation function from react-i18next
 * @param assetType - The type of asset (e.g., 'Video', 'Image', 'Audio')
 * @param extension - Optional file extension to include in the title
 */
export const getUntitledAssetString = (
  t: (key: string) => string,
  assetType: string,
  extension: string,
) =>
  `${t('blox.general.untitled')} ${assetType} ${t('blox.general.asset')} ${
    extension ? `(${extension})` : ''
  } - ${new Date().toString()}`;

// Helper function to format date/time
export const formatDateTime = (timestamp: any) => {
  const date = new Date(timestamp);
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
};

// Helper function to format date for file names
export const formatFileDate = (timestamp: number) => {
  const date = new Date(timestamp);
  return date.toISOString().split('T')[0]; // YYYY-MM-DD format
};

// Helper function to convert response to Blob
export const convertToBlob = (response: any): Blob => {
  if (response instanceof Blob) {
    return response;
  } else if (response?.data instanceof Blob) {
    return response.data;
  } else if (typeof response === 'string') {
    return new Blob([response], { type: 'text/csv' });
  } else if (response?.data) {
    return new Blob([response.data], { type: 'text/csv' });
  } else {
    throw new Error('Unexpected response format');
  }
};

// Helper function to generate filename with domain and date
// Note: Usable for CSV only at this time
export const generateFilename = (
  domain: string,
  timestamp: number,
  identifier: string,
  t: TFunction,
): string => {
  const fileDate = formatFileDate(timestamp);
  const cleanDomain = domain.replace(/^https?:\/\//, '').replace(/\/$/, '');
  const safeIdentifier = identifier.toLowerCase().replace(/\s+/g, '-');
  return `${t(
    'blox.download_menu.file.prefix',
  )}-${cleanDomain}-${fileDate}-${safeIdentifier}-${t(
    'blox.download_menu.file.suffix',
  )}.csv`;
};

// Triggers a file download from a Blob using a temporary anchor element.
export const triggerBlobDownload = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(url);
};

/**
 * Normalize a value into a safe data-testid segment by replacing any character
 * that is not alphanumeric, underscore, or hyphen with a hyphen.
 * @param value - The raw value to normalize (string, number, or nullish).
 * @returns A sanitized string safe for use inside a data-testid attribute.
 */
export function normalizeTestIdValue(value?: string | number | null): string {
  return String(value ?? 'unknown').replace(/[^a-zA-Z0-9_-]+/g, '-');
}
